import type { BriefComment } from '@dyspatch/core';
import { X } from 'lucide-react';
import { useState } from 'react';
import { Markdown } from '@/components/common/Markdown';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Finding } from '@/features/findings/Finding';
import { useHubAction } from '@/hooks/use-hub-action';
import { api } from '@/lib/api';
import { ago, minutesSince } from '@/lib/format';

/**
 * The conversation before Go.
 *
 * A note aimed at a step folds into that step's spec when the plan is approved,
 * so the target is shown as a removable chip rather than buried in a select:
 * what you are about to change is visible while you type it. After Go the box
 * goes away, because the conversation moves to the pull request.
 *
 * It is a region of the job page now rather than the third scroller in a 400px
 * rail, so the compose box sits under the thread at full width and a note is
 * read at the size the rest of the page is read at.
 *
 * An agent finding is one of these comments (it carries the task it came
 * from), but it is not read like one: it has a verb, and once filed an issue
 * link instead of one. `Finding` draws that - the same component the task
 * pane uses - so a finding reads as a finding on both pages rather than as a
 * comment with a task key and nothing to do about it (docs/JOB-LIFECYCLE.md 2.8).
 */
export function JobComments({
  briefId,
  comments,
  commentable,
  target,
  onTarget,
}: {
  briefId: string;
  comments: readonly BriefComment[];
  commentable: boolean;
  /** The step a note is aimed at, or null for the whole plan. */
  target: string | null;
  onTarget: (key: string | null) => void;
}) {
  const [body, setBody] = useState('');
  const send = useHubAction(
    (text: string, taskKey: string | null) =>
      api.commentOnBrief(briefId, text, taskKey ?? undefined),
    {
      onDone: () => {
        setBody('');
        onTarget(null);
      },
      // The box keeps what you typed and prints the refusal under it.
      inlineError: true,
    },
  );

  return (
    <>
      {comments.length ? (
        <ul className="divide-line divide-y">
          {comments.map((comment) =>
            comment.authorKind === 'agent' ? (
              <li key={comment.id} className="px-gutter py-2.5">
                <Finding note={comment} />
              </li>
            ) : (
              <li key={comment.id} className="px-gutter py-2.5">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
                  <span className="font-medium">{comment.author}</span>
                  {comment.taskKey ? (
                    <span className="bg-raised text-fg-2 rounded-sm px-1 font-mono text-2xs">
                      {comment.taskKey}
                    </span>
                  ) : null}
                  {/* When it was said. A thread with no times on it reads as
                      one conversation however many days it spans. */}
                  <span className="text-fg-3 text-2xs">{ago(minutesSince(comment.createdAt))}</span>
                </div>
                <Markdown source={comment.body} className="mt-1 max-w-[88ch] text-sm" />
              </li>
            ),
          )}
        </ul>
      ) : (
        <p className="text-fg-3 px-gutter py-3 text-sm">No comments yet.</p>
      )}

      {commentable ? (
        <form
          className="dy-inset mx-gutter mb-3 px-gutter py-2.5"
          onSubmit={(event) => {
            event.preventDefault();
            const text = body.trim();
            if (!text) return;
            send.run(text, target);
          }}
        >
          <div className="mb-1.5 flex items-center gap-1.5">
            {target ? (
              <span className="bg-gate-bg text-gate inline-flex h-[18px] items-center gap-1 rounded-sm px-1.5 font-mono text-2xs">
                {target}
                <button
                  type="button"
                  onClick={() => onTarget(null)}
                  aria-label="Comment on the whole plan"
                  className="hover:opacity-70"
                >
                  <X className="size-2.5" aria-hidden />
                </button>
              </span>
            ) : (
              <span className="text-fg-3 text-2xs">whole plan</span>
            )}
          </div>
          <div className="flex max-w-[88ch] items-center gap-1.5">
            <Input
              value={body}
              onChange={(event) => setBody(event.target.value)}
              maxLength={4000}
              placeholder="Note on the plan…"
              aria-label="Comment on the plan"
              className="flex-1"
            />
            <Button type="submit" size="sm" variant="outline" disabled={send.busy}>
              {send.busy ? 'Sending…' : 'Send'}
            </Button>
          </div>
          {send.error ? (
            <p className="text-bad pt-1.5 text-xs" role="alert">
              {send.error}
            </p>
          ) : null}
        </form>
      ) : (
        <p className="text-fg-3 border-t px-gutter py-2.5 text-xs">
          After Go the conversation moves to the PR.
        </p>
      )}
    </>
  );
}
